// Item feed — tier selector, cards (grid), table (list)

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Badge, Segmented, ValueTierDisplay, ValueTierGlyph } from "./atoms.jsx";
import {
  SORT_OPTIONS,
  TIER_LABELS,
  dealTierLabel,
  dealTierPresentation,
  dealTierThresholdText,
  dealTierTooltip,
} from "./data.jsx";
import { I } from "./icons.jsx";
import { WB_LABEL as WEIGHT_BASIS_LABELS, weightBasisDisplay } from "./weightBasis.jsx";
import "./feed.css";

const money = (n) => n == null ? "—" : "$" + Number(n).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0});
const moneyC = (n) => n == null ? "—" : "$" + Number(n).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
const shippingLine = (item, { freeLabel = "free ship" } = {}) => {
  const hasKnownShipping = item.shippingStatus
    ? item.shippingStatus === "known" && item.shipping != null
    : item.shipping != null;
  const itemPrice = item.price != null ? `item ${money(item.price)}` : "item price unknown";
  if (!hasKnownShipping) return `${itemPrice} + ship unknown`;
  if (Number(item.shipping) === 0) return `${itemPrice} + ${freeLabel}`;
  return `${itemPrice} + ${moneyC(item.shipping)} ship`;
};
const formatWeightGrams = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};
const goldWeightText = (item, { spaced = false } = {}) => {
  const grams = formatWeightGrams(item && item.goldWeight);
  if (grams == null) return "—";
  return `${grams}${spaced ? " " : ""}g${item.goldWeightDerived ? "*" : ""}`;
};
const ageStr = (min) => {
  if (min < 1) return "<1m"; if (min < 60) return `${Math.round(min)}m`;
  if (min < 24*60) return `${Math.round(min/60)}h`; return `${Math.round(min/60/24)}d`;
};
const listedAgeText = (item) => `Listed: ${ageStr(item.ageMin)} ago`;
const sellerFeedbackText = (item) => {
  const parts = [];
  if (item.sellerFeedbackPercent != null) {
    const n = Number(item.sellerFeedbackPercent);
    parts.push(Number.isFinite(n) ? `${n.toFixed(1)}%` : `${item.sellerFeedbackPercent}%`);
  }
  if (item.sellerFeedbackScore != null) {
    parts.push(Number(item.sellerFeedbackScore).toLocaleString());
  }
  return parts.join(" - ");
};
const sellerTitle = (item) => {
  const name = item.seller ? `@${item.seller}` : "Seller unavailable";
  const feedback = sellerFeedbackText(item);
  return feedback ? `${name} - ${feedback}` : name;
};
const TIER_LABEL = TIER_LABELS;
const tierLabel = (code, fallback) =>
  dealTierLabel(code, fallback);
const tierThresholdText = (code, fallback) =>
  dealTierThresholdText(code, fallback);
const tierTooltip = (code) =>
  dealTierTooltip(code);
const tierPresentation = (code, fallback) =>
  dealTierPresentation(code, fallback);
const WB_LABEL = WEIGHT_BASIS_LABELS;
const getWeightBasisDisplay = weightBasisDisplay;
const weightBasisClass = (value) => String(value || "none").replace(/[^a-z0-9_-]/gi, "-");
const CONF_SHORT = { high:"High conf", medium:"Med conf", low:"Low conf" };
const itemTypeLabel = (value) => String(value || "item").replace(/[_-]+/g, " ");
const itemTitleId = (item) =>
  `item-title-${String(item.id ?? item.itemId ?? item.title ?? "item").replace(/[^a-z0-9_-]/gi, "-")}`;
const thumbPlaceholderLabel = (item) =>
  `${(item.itemType || "item").toUpperCase()}${item.karat ? ` \u00b7 ${item.karat}K` : ""}`;
const isAccentThumbPlaceholder = (item) => {
  const priced = item.pricingStatus === "priced" || item.pricingStatus === "priced_review_required";
  return item.primaryMetal === "gold" && Number(item.karat) >= 14 && priced;
};
const thumbPlaceholderClass = (item) =>
  `thumb-ph${isAccentThumbPlaceholder(item) ? " accent" : ""}`;
const LIST_THUMBNAIL_PREVIEW_MARGIN = 12;
const LIST_THUMBNAIL_PREVIEW_GAP = 14;
const LIST_THUMBNAIL_PREVIEW_WIDTH = 340;
const LIST_THUMBNAIL_PREVIEW_HEIGHT = 260;

const replaceImageWithPlaceholder = (node, item) => {
  const fallback = document.createElement("div");
  fallback.className = thumbPlaceholderClass(item);
  fallback.setAttribute("data-testid", "thumb-placeholder");
  fallback.setAttribute("role", "img");
  fallback.setAttribute("aria-label", `${item.title || "Listing"} image unavailable`);
  fallback.style.height = "100%";
  fallback.style.width = "100%";
  const tag = document.createElement("span");
  tag.className = "tag";
  tag.textContent = thumbPlaceholderLabel(item);
  fallback.appendChild(tag);
  node.replaceWith(fallback);
};

const clamp = (value, minimum, maximum) =>
  Math.max(minimum, Math.min(value, maximum));

const listThumbnailPreviewPosition = (clientX, clientY) => {
  if (typeof window === "undefined") {
    return { left: clientX + LIST_THUMBNAIL_PREVIEW_GAP, top: clientY + LIST_THUMBNAIL_PREVIEW_GAP };
  }

  const margin = LIST_THUMBNAIL_PREVIEW_MARGIN;
  const gap = LIST_THUMBNAIL_PREVIEW_GAP;
  const width = Math.min(LIST_THUMBNAIL_PREVIEW_WIDTH, Math.max(0, window.innerWidth - margin * 2));
  const height = Math.min(LIST_THUMBNAIL_PREVIEW_HEIGHT, Math.max(0, window.innerHeight - margin * 2));
  const maxLeft = Math.max(margin, window.innerWidth - width - margin);
  const maxTop = Math.max(margin, window.innerHeight - height - margin);
  let left = clientX + gap;
  let top = clientY + gap;

  if (left + width + margin > window.innerWidth) {
    left = clientX - width - gap;
  }
  if (top + height + margin > window.innerHeight) {
    top = window.innerHeight - height - margin;
  }

  return {
    left: Math.round(clamp(left, margin, maxLeft)),
    top: Math.round(clamp(top, margin, maxTop)),
  };
};

const previewSource = (thumb) =>
  thumb?.currentSrc || thumb?.getAttribute?.("src") || thumb?.src || "";

const useListThumbnailPreview = () => {
  const [preview, setPreview] = useState(null);

  const hidePreview = useCallback(() => {
    setPreview(null);
  }, []);

  const showPreview = useCallback((thumb, clientX, clientY) => {
    const src = previewSource(thumb);
    if (!src) return;
    const rect = thumb.getBoundingClientRect();
    const x = Number.isFinite(clientX) ? clientX : rect.right;
    const y = Number.isFinite(clientY) ? clientY : rect.top;
    setPreview({
      src,
      alt: thumb.alt || "Item image preview",
      ...listThumbnailPreviewPosition(x, y),
    });
  }, []);

  const updatePreviewPosition = useCallback((clientX, clientY) => {
    setPreview((current) =>
      current ? { ...current, ...listThumbnailPreviewPosition(clientX, clientY) } : current,
    );
  }, []);

  useEffect(() => {
    if (!preview) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") hidePreview();
    };
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", hidePreview);
    window.addEventListener("scroll", hidePreview, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", hidePreview);
      window.removeEventListener("scroll", hidePreview, true);
    };
  }, [hidePreview, preview]);

  const thumbProps = {
    onPointerEnter: (event) => {
      if (event.pointerType === "touch") return;
      showPreview(event.currentTarget, event.clientX, event.clientY);
    },
    onPointerMove: (event) => {
      if (event.pointerType === "touch") return;
      updatePreviewPosition(event.clientX, event.clientY);
    },
    onPointerLeave: hidePreview,
    onPointerDown: hidePreview,
    onFocus: (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      showPreview(event.currentTarget, rect.right, rect.top);
    },
    onBlur: hidePreview,
  };

  return { preview, thumbProps, hidePreview };
};

const ListThumbnailPreview = ({ preview, onImageError }) => {
  if (!preview || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="list-thumbnail-preview visible"
      data-testid="list-thumbnail-preview"
      style={{ left: `${preview.left}px`, top: `${preview.top}px` }}
      aria-hidden="true"
    >
      <img
        data-testid="list-thumbnail-preview-image"
        src={preview.src}
        alt={preview.alt}
        onError={onImageError}
      />
    </div>,
    document.body,
  );
};

const ThumbPh = ({ item, src, alt, imageProps }) => {
  // Real listing photo when the API gave us one (proxied_url); else placeholder.
  const imageSrc = src || item.imageUrl;
  if (imageSrc) {
    const { onError, className, ...restImageProps } = imageProps || {};
    return (
      <img
        {...restImageProps}
        className={`thumb-img${className ? ` ${className}` : ""}`}
        data-testid="thumb-image"
        src={imageSrc}
        alt={alt || item.title}
        loading="lazy"
        onError={(e) => {
          onError?.(e);
          if (!e.defaultPrevented) replaceImageWithPlaceholder(e.currentTarget, item);
        }}
      />
    );
  }
  return (
    <div className={thumbPlaceholderClass(item)} data-testid="thumb-placeholder" style={{height:"100%", width:"100%"}}>
      <span className="tag">{thumbPlaceholderLabel(item)}</span>
    </div>
  );
};

const DealTierBadge = ({ tier }) => {
  if (!tier) return null;
  const presentation = tierPresentation(tier);
  const hasValueGlyph = Boolean(presentation.valueTier);
  return (
    <Badge
      tone={hasValueGlyph ? "gold" : "neutral"}
      icon={hasValueGlyph ? <ValueTierGlyph tier={presentation.valueTier} size="badge" testId="deal-tier-badge-glyph" /> : null}
      title={presentation.tooltip || presentation.ariaLabel}
      ariaLabel={presentation.ariaLabel}
      testId="deal-tier-badge"
      className={`deal-tier-badge${hasValueGlyph ? " has-value-glyph" : ""}`}
    >
      {presentation.label}
    </Badge>
  );
};

const noRatioLabel = (item) =>
  (item.reasonCodes || []).includes("missing_weight") ? "Needs weight" : "No value score";

const RatioStrip = ({ item }) => {
  if (item.ratio == null) {
    return <div className="ratio-strip nw" data-testid="ratio-strip"><span className="rv">{noRatioLabel(item)}</span></div>;
  }
  const below = item.ratio < 1.0;
  return (
    <div className={`ratio-strip ${below ? "below" : ""}`} data-testid="ratio-strip">
      <span className="rk">price ÷ melt</span>
      <span className="rv">{item.ratio.toFixed(2)}<span className="x">×</span></span>
    </div>
  );
};

const AUTHENTICITY_BADGE_SRC = "assets/authenticity-guarantee-badge.png?v=blue-check-20260627";

const AuthenticityBadgeIcon = () => (
  <img
    className="auth-badge"
    data-testid="authenticity-badge"
    src={AUTHENTICITY_BADGE_SRC}
    alt="eBay Authenticity Guarantee"
    title="eBay Authenticity Guarantee"
    loading="lazy"
  />
);

const ConfSig = ({ level }) => (
  <span className={`sig conf-${level}`} data-testid="confidence-chip" data-conf={level}><span className="sd"/>{CONF_SHORT[level]}</span>
);

const ItemTypeSig = ({ type }) => (
  <span className="sig item-type" data-testid="item-type-chip">{itemTypeLabel(type)}</span>
);

const ReviewSig = () => (
  <span className="sig review" data-testid="review-chip">review</span>
);

const WeightBasisSig = ({ basis }) => {
  const display = getWeightBasisDisplay(basis);
  return (
    <span
      className={`sig wb-${weightBasisClass(display.code)}`}
      data-testid="weight-basis-chip"
      title={display.title}
      aria-label={display.title}
    >
      {display.icon === "check" ? <I.check size={9}/> : <I.spark size={9}/>}
      {display.compactLabel}
    </span>
  );
};

const SaveBtn = ({ item, signedIn, onSave }) => {
  return (
    <button
      className="save-btn locked"
      data-testid="save-button"
      type="button"
      title="Save coming soon"
      aria-label="Save item (coming soon)"
      onClick={(e)=>{e.stopPropagation(); onSave?.(item, e);}}
    >
      <I.star size={13}/>
    </button>
  );
};

const openItemFromKeyboard = (event, item, onOpen) => {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  onOpen?.(item);
};

const openItemFromButton = (event, item, onOpen) => {
  event.stopPropagation();
  onOpen?.(item);
};

const entryDelayStyle = (delayMs = 0) => ({
  "--entry-delay": `${Math.max(Number(delayMs) || 0, 0)}ms`,
});

const ItemCard = ({ item, justIn, entryDelay = 0, onOpen, signedIn, onSave }) => {
  const priced = item.meltValue != null;
  const isSTier = item.valueTier === "S";
  const titleId = itemTitleId(item);
  return (
    <article
      className={`item t-${item.tier || "none"} ${isSTier ? "value-tier-s" : ""} ${item.requiresReview ? "review" : ""} ${justIn ? "just-in" : ""}`}
      style={justIn ? entryDelayStyle(entryDelay) : undefined}
      data-testid="item-card"
      data-value-tier={item.valueTier || ""}
      aria-labelledby={titleId}
      onClick={() => onOpen?.(item)}
    >
      {isSTier && <span className="s-tier-border" data-testid="s-tier-border" aria-hidden="true" />}
      <div className="item-thumb">
        <ThumbPh item={item}/>
        <div className="item-badges">
          <DealTierBadge tier={item.tier}/>
          {item.nearMiss && <Badge tone="info">Near-miss</Badge>}
          {item.bestOffer && <Badge tone="info">Accepts offers</Badge>}
          {item.requiresReview && <Badge tone="hot" icon={<I.eye size={9}/>}>Review</Badge>}
        </div>
        <div className="item-count" data-testid="item-image-count"><I.photo size={9}/> {item.images}</div>
        {item.authenticityGuarantee && <AuthenticityBadgeIcon/>}
        <RatioStrip item={item}/>
      </div>
      <div className="item-body">
        {isSTier && <span className="s-tier-shine" data-testid="s-tier-shine" aria-hidden="true" />}
        <div className="item-head">
          <button
            id={titleId}
            className="item-title item-title-button"
            type="button"
            aria-label={`Open item details for ${item.title}`}
            aria-haspopup="dialog"
            onClick={(event) => openItemFromButton(event, item, onOpen)}
          >
            {item.title}
          </button>
          <div className="item-price">
            <div className="px">{money(item.allIn)}</div>
            <div className="ship">{shippingLine(item)}</div>
          </div>
        </div>

        <div className="item-attrs">
          <div className="a"><span className="k">Karat</span><span className="v gold">{item.karat ? `${item.karat}K` : "—"}</span></div>
          <div className="a"><span className="k">Gold wt</span><span className="v" data-testid="gold-weight-value">{goldWeightText(item)}</span></div>
          <div className="a"><span className="k">Melt est.</span><span className="v">{priced ? money(item.meltValue) : "—"}</span></div>
          <div className="a"><span className="k">Value tier</span><span className="v"><ValueTierDisplay tier={item.valueTier}/></span></div>
        </div>

        <div className="signal-row">
          <ConfSig level={item.overallConfidence}/>
          <WeightBasisSig basis={item.weightBasis}/>
          <span className="sig" title={`Coverage: ${item.coverage}`}>{item.coverage}</span>
          <ItemTypeSig type={item.itemType}/>
        </div>

        <div className="item-foot">
          <div className="meta">
            <span className="seller-block" title={sellerTitle(item)}>
              <span className="seller">{item.seller ? `@${item.seller}` : "eBay listing"}</span>
              {sellerFeedbackText(item) && <span className="seller-feedback">{sellerFeedbackText(item)}</span>}
            </span>
            <span className="age" data-testid="item-age">{listedAgeText(item)}</span>
          </div>
          <SaveBtn item={item} signedIn={signedIn} onSave={onSave}/>
        </div>
      </div>
    </article>
  );
};

const ListView = ({ items, justInIds, justInDelays = new Map(), onOpen, signedIn }) => {
  const preview = useListThumbnailPreview();

  return (
  <div className="list">
    <div className="list-grid list-hdr">
      <div></div>
      <div>Listing</div>
      <div>Karat</div>
      <div>Gold wt</div>
      <div>Melt est.</div>
      <div className="sortable">Price ÷ melt <I.chev size={10}/></div>
      <div className="list-value-tier">Value tier</div>
      <div>All-in</div>
      <div>Seller</div>
      <div style={{textAlign:"right"}}>Age</div>
    </div>
    {items.map((it) => {
      const justIn = justInIds.has(it.id);
      return (
      <div
        key={it.id}
        className={`list-grid list-row t-${it.tier || "none"} ${justIn ? "just-in" : ""}`}
        style={justIn ? entryDelayStyle(justInDelays.get(it.id) || 0) : undefined}
        data-testid="item-row"
        tabIndex={0}
        role="button"
        aria-haspopup="dialog"
        aria-label={`Open item details for ${it.title}`}
        onClick={() => onOpen?.(it)}
        onKeyDown={(event) => openItemFromKeyboard(event, it, onOpen)}
      >
        <div className="list-thumb"><ThumbPh item={it} imageProps={preview.thumbProps}/></div>
        <div className="lt">
          {it.title}
          <span className="lsub">
            <DealTierBadge tier={it.tier}/>
            {it.bestOffer && <Badge tone="info">Accepts offers</Badge>}
            <ItemTypeSig type={it.itemType}/>
            <span className={`sig conf-${it.overallConfidence}`} data-testid="confidence-chip" data-conf={it.overallConfidence} style={{padding:"1px 5px"}}><span className="sd"/>{it.overallConfidence}</span>
            {it.requiresReview && <ReviewSig/>}
            {justIn && <span className="list-just-in">JUST IN</span>}
          </span>
        </div>
        <div style={{color:"var(--accent-deep)", fontWeight:600}}>{it.karat ? `${it.karat}K` : "—"}</div>
        <div data-testid="gold-weight-value">{goldWeightText(it)}</div>
        <div>{money(it.meltValue)}</div>
        <div className={`list-ratio ${it.ratio != null && it.ratio < 1 ? "below" : ""}`}>{it.ratio != null ? `${it.ratio.toFixed(2)}×` : noRatioLabel(it)}</div>
        <div className="list-value-tier" data-testid="list-value-tier"><ValueTierDisplay tier={it.valueTier}/></div>
        <div style={{color:"var(--ink)", fontWeight:600}}>{money(it.allIn)}</div>
        <div className="list-seller" data-testid="list-seller" title={sellerTitle(it)}>
          {it.seller ? (
            <>
              <span className="seller-name">@{it.seller}</span>
              {sellerFeedbackText(it) && <span className="seller-meta">{sellerFeedbackText(it)}</span>}
            </>
          ) : (
            <span className="seller-name">{"\u2014"}</span>
          )}
        </div>
        <div data-testid="item-age" style={{textAlign:"right", color:"var(--muted)"}}>{ageStr(it.ageMin)}</div>
      </div>
      );
    })}
    <ListThumbnailPreview preview={preview.preview} onImageError={preview.hidePreview}/>
  </div>
  );
};

// Tier feed selector
const FEED_CONFIG = [
  { key:"todays_deals", fallbackName:"Today's Deals", fallbackThresh:"last 24h + <= 1.40x melt", dot:"tier-dot-today" },
  { key:"deep_discount", fallbackName:"Under Melt", fallbackThresh:"≤ 1.00x melt", dot:"tier-dot-deep" },
  { key:"steal", fallbackName:"Great Deal", fallbackThresh:"≤ 1.20x melt", dot:"tier-dot-steal" },
  { key:"deal", fallbackName:"Good Deal", fallbackThresh:"≤ 1.40x melt", dot:"tier-dot-deal" },
  { key:"negotiable", fallbackName:"Accepts Offers", fallbackThresh:"Best Offer + ≤ 1.40x melt", dot:"tier-dot-negotiable" },
  { key:"unscored_gold", fallbackName:"Unscored Gold", fallbackThresh:"no value scoring", dot:"tier-dot-nw" },
  { key:"all", name:"All items", thresh:"parsed catalog", dot:"" },
];
const VALUE_FEEDS = ["deep_discount", "steal", "deal"];
const feedThresholdText = (feed, fallback) =>
  feed === "todays_deals" ? `last 24h + ${tierThresholdText(feed, "<= 1.40x melt")}` : tierThresholdText(feed, fallback);

const TierBar = ({ feed, setFeed, counts }) => (
  <div className="tierbar" data-testid="tier-bar">
    {FEED_CONFIG.map(f => {
      const selected = feed === f.key;
      const presentation = f.key === "all"
        ? { label: f.name, valueTier: null }
        : tierPresentation(f.key, f.fallbackName);
      const name = presentation.label;
      const thresh = f.key === "all" ? f.thresh : feedThresholdText(f.key, f.fallbackThresh);
      return (
        <button
          key={f.key}
          className={`tier-tab ${selected ? "active" : ""}`}
          data-testid={`tier-tab-${f.key}`}
          aria-pressed={selected}
          onClick={() => setFeed(f.key)}
        >
          <span className="tt-name">
            {presentation.valueTier
              ? <ValueTierGlyph tier={presentation.valueTier} size="tab" testId="deal-tier-tab-glyph" />
              : f.dot && <span className={`tt-dot ${f.dot}`}/>}
            {name}
            <span className="tt-count">{counts[f.key] != null ? counts[f.key] : "—"}</span>
          </span>
          <span className="tt-thresh">{thresh}</span>
        </button>
      );
    })}
    <div className="tierbar-spacer"/>
    <div className="tierbar-aside"/>
  </div>
);

const FEED_COPY = {
  all: { h:<>Browse <em>- all items</em></>, d:"Completed parsed listings across priced and unscored public outcomes." },
};

const feedCopyForDisplay = (feed) => {
  if (feed === "todays_deals") {
    return {
      h: <>{tierLabel("todays_deals")} <em>- fresh value finds</em></>,
      d: tierTooltip("todays_deals") || "Listed in the last 24 hours and priced inside the Good Deal band.",
    };
  }
  if (VALUE_FEEDS.includes(feed)) {
    const label = tierLabel(feed);
    const sub = feed === "deep_discount" ? "priced below gold melt value" : feed === "steal" ? "strong value candidates" : "solid value range";
    return {
      h: <>{label} <em>- {sub}</em></>,
      d: tierTooltip(feed) || `Asking price ${tierThresholdText(feed)} based on estimated melt value.`,
    };
  }
  if (feed === "negotiable") {
    return {
      h: <>{tierLabel("negotiable")} <em>- negotiable listings</em></>,
      d: tierTooltip("negotiable") || "Listings where the seller has enabled Best Offer and the asking price is inside the Good Deal band.",
    };
  }
  if (feed === "near") {
    return {
      h: <>{tierLabel("near")} <em>- watchlist</em></>,
      d: tierTooltip("near") || "Items just outside the selected value band.",
    };
  }
  if (feed === "unscored_gold" || feed === "needs") {
    return {
      h: <>{tierLabel("unscored_gold")} <em>- no value scoring</em></>,
      d: tierTooltip("unscored_gold") || "Confirmed solid-gold listings that do not currently have a deal ratio.",
    };
  }
  return FEED_COPY.all;
};

const FeedHeader = ({
  feed,
  sort,
  setSort,
  count,
  liveOn,
  view,
  setView,
  searchTerm,
  searchMeta,
  sortEnabled = true,
  sortDisabledReason = "",
}) => {
  const total = searchMeta && searchMeta.totalCount != null ? searchMeta.totalCount : count;
  const copy = searchTerm
    ? { h:<>Search <em>"{searchTerm}"</em></>, d:`${total} live result${total === 1 ? "" : "s"} from the public catalog.` }
    : feedCopyForDisplay(feed);
  const disabledReasonId = sortEnabled ? undefined : "sort-disabled-reason";
  const disabledSortLabels = {
    listing_time_desc: "Listing time",
    search_relevance: "Search relevance",
  };
  const sortOptions = sortEnabled
    ? SORT_OPTIONS
    : [{ value: sort, label: disabledSortLabels[sort] || "Server order" }];
  return (
    <div className="feed-top">
      <div className="feed-title">
        <h1>{copy.h}</h1>
        <div className="sub" style={{fontFamily:"var(--font-ui)"}}>{copy.d}</div>
      </div>
      <div className="feed-ctrls">
        <select
          className="select"
          data-testid="sort-select"
          style={{width:"auto", height:30}}
          value={sort}
          onChange={e => setSort(e.target.value)}
          aria-label="Sort feed"
          aria-describedby={disabledReasonId}
          disabled={!sortEnabled}
          title={!sortEnabled ? sortDisabledReason : undefined}
        >
          {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {!sortEnabled ? <span id={disabledReasonId} className="sr-only">{sortDisabledReason}</span> : null}
        <Segmented value={view} onChange={setView} options={[
          { value: "grid", icon: <I.grid size={13} style={{marginRight:3}}/>, label: "Grid" },
          { value: "list", icon: <I.list size={13} style={{marginRight:3}}/>, label: "List" },
        ]}/>
      </div>
    </div>
  );
};

const FeedFooter = ({
  shown,
  total,
  hasMore,
  loadingMore,
  stale,
  nextPageError,
  onLoadMore,
  onRetryMore,
  searchCapped,
}) => {
  const totalNumber = total == null || total === "" ? null : Number(total);
  const hasKnownTotal = Number.isFinite(totalNumber);
  const hasDisplayableTotal = hasKnownTotal && totalNumber >= shown;
  const shownText = hasDisplayableTotal
    ? `${shown} of ${totalNumber} shown`
    : `${shown} shown`;
  const status = nextPageError
    ? "Could not load more"
    : loadingMore
      ? "Loading more..."
      : searchCapped
        ? "Search limit reached; refine search"
        : stale
          ? "Refresh failed; showing cached results"
          : hasMore
            ? "More results available"
            : (!hasMore && (!hasKnownTotal || shown >= totalNumber))
            ? "All results shown"
            : "Showing current results";
  return (
    <div className="feed-end" aria-live="polite">
      <div className="rule"/>
      <div className="feed-footer-main" data-testid="feed-refresh-footer">
        <span className="mono feed-footer-count">{shownText}</span>
        <span className={`feed-footer-status ${stale || nextPageError ? "warn" : ""}`}>{status}</span>
        {hasMore && !nextPageError ? (
          <button
            className="btn sm"
            data-testid="load-more-button"
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading..." : "Load more"}
          </button>
        ) : null}
        {nextPageError ? (
          <button
            className="btn sm"
            data-testid="load-more-retry"
            type="button"
            onClick={onRetryMore}
          >
            Retry
          </button>
        ) : null}
      </div>
      <div className="rule"/>
    </div>
  );
};

// states
const EmptyState = ({ feed }) => (
  <div className="feed-state" data-testid="empty-state">
    <div className="icon-wrap"><I.search size={20}/></div>
    <h3>No items in this band right now</h3>
    <p>{feed === "todays_deals" ? "No fresh S, A, or B tier listings have landed in the last 24 hours." : feed === "unscored_gold" || feed === "needs" ? "Every recent solid-gold item has a deal ratio right now." : "Nothing matches these thresholds yet. The live stream will drop new matches in as they're analyzed."}</p>
  </div>
);

export {
  AUTHENTICITY_BADGE_SRC,
  AuthenticityBadgeIcon,
  ConfSig,
  DealTierBadge,
  EmptyState,
  FEED_CONFIG as FEEDS,
  FEED_COPY,
  FeedFooter,
  FeedHeader,
  ItemCard,
  ListView,
  SaveBtn,
  TIER_LABEL,
  ThumbPh,
  TierBar,
  WB_LABEL,
  ageStr,
  formatWeightGrams,
  goldWeightText,
  money,
  moneyC,
  shippingLine,
};
